import { Card, Checkbox, Grid, Icon, IconButton, InputLabel, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDModalMenus from "components/MDModalMenus";
import MDModalPerfis from "components/MDModalPerfis";
import MDInput from "components/MDInput";
import MDSelect from "components/MDSelect";
import MDTypography from "components/MDTypography";
import MDViewLog from "components/MDViewLog";
import { useMaterialUIController } from "context";
import DataTable from "examples/Tables/DataTable";
import DefaultCell from "layouts/dashboards/sales/components/DefaultCell";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { toDateTime } from "utils/DateUtils";
import { trueOrFalseEnum } from "utils/Enums";
import { errorAlert, successAlert, verifyVariable, infoAviso } from "utils/Functions";
import { get, post, postNoResponse } from "utils/WebService";
import UserCreateEdit from "./UserCreateEdit";
import { useUser } from "context/UserContext";


export default function UsersConsult() {

    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const { userPerfils } = useUser();
    const isAdmin = userPerfils && userPerfils.some(p => p?.tipoPerfil === 4 || p?.nome?.toLowerCase().includes('admin') || (typeof p === 'string' && p.toLowerCase().includes('admin')));
    
    const tableState = useRef({
        pageSize: 10,
        pageIndex: 0,
        sorting: [],
        filtered: [],
        profileIdList: []
    });

    const [isLoadingPerfis, setIsLoadingPerfis] = useState(true);
    const [profileOptions, setProfileOptions] = useState([]);

    const [selectedUserId, setSelectedUserId] = useState(null);
    const [selectedIdInclusao, setSelectedIdInclusao] = useState(null);
    const [isCreateEdit, setIsCreateEdit] = useState(false);
    const [loadingTable, setLoadingTable] = useState(true);
    const [userDataPages, setUserDataPages] = useState(0);
    const [userData, setUserData] = useState([]);
    const [isLog, setIsLog] = useState(false);
    const [isModalTable, setIsModalTable] = useState(false);
    const [dadosModalTable, setDadosModalTable] = useState(null);
    const [headerModal, setHeaderModal] = useState(null);
    const [refresh, setRefresh] = useState(false);
    const [recordsTotal, setRecordsTotal] = useState(0);
    const [dadosModalFuncinalidades, setDadosModalFuncinalidades] = useState(null);
    const [isModalFuncionalidades, setIsModalFuncionalidades] = useState(false);
    const [aplicar, setAplicar] = useState(false);

    const [filters, setFilters] = useState({
        nome: "",
        email: "",
        perfils: [],
        bloqueado: null,
        ativo: trueOrFalseEnum[1],
    });

    useEffect(() => {
        let filtered = [];
        filtered.push({ id: 'ativo', value: true });
        fetchData({ ...tableState, filtered });

        get("/profiles/list-all")
            .then(({ list }) => {
                setProfileOptions(list);
                setIsLoadingPerfis(false);
            }).catch(() => {
                setProfileOptions([]);
                setIsLoadingPerfis(false);
            });
    }, []);

    const fetchData = useCallback(({
        pageSize,
        pageIndex,
        sorting,
        filtered,
        profileIdList }) => {
        setLoadingTable(true);

        const updatedTableState = {
            pageSize: pageSize ? pageSize : tableState.current.pageSize,
            pageIndex: (pageIndex !== undefined && pageIndex !== null) ? pageIndex : tableState.current.pageIndex,
            sorting: sorting ? sorting : tableState.current.sorting,
            filtered: filtered ? filtered : tableState.current.filtered,
            profileIdList: (profileIdList && profileIdList.length > 0) ? profileIdList : tableState.current.profileIdList
        };
        tableState.current = updatedTableState;
        post("/users/list", {
            page: updatedTableState.pageIndex,
            pageSize: updatedTableState.pageSize,
            sorted: updatedTableState.sorting,
            filtered: updatedTableState.filtered,
            profileIdList: updatedTableState.profileIdList
        }).then(({ data, pages, recordsTotal }) => {
            setUserDataPages(pages);
            setUserData(data);
            setRecordsTotal(recordsTotal);
            setLoadingTable(false);
            setAplicar(false);
        }).catch(() => {
            setLoadingTable(false)
            setAplicar(false);
        });

        setRefresh(false);

    }, [refresh]);

    const backFunction = () => {
        setIsCreateEdit(false);
        setSelectedUserId(null);
        setSelectedIdInclusao(null);
    }

    function recoverPassword(email) {
        postNoResponse(`/users/public/reset-password?email=${email}`)
            .then(() => successAlert("Foi enviado um e-mail ao usuário para resetar a senha!"))
            .catch(() => errorAlert("Erro ao enviar o e-mail, tente novamente mais tarde."))
    }

    function deleteUser(id) {
        infoAviso("Deseja realmente excluir este usuário?", () => {
            postNoResponse(`/users/delete/${id}`)
                .then(() => {
                    successAlert("Usuário excluído com sucesso!");
                    handleRefresh();
                })
                .catch((e) => {
                    if (e && e.response && e.response.status === 409) {
                        errorAlert("Este usuário possui vínculos no sistema e não pode ser excluído. Considere inativá-lo.");
                    } else {
                        errorAlert("Erro ao excluir usuário.");
                    }
                });
        });
    }

    const userColumns = useMemo(() => [
        {
            header: "cód.",
            accessorKey: "cvCodUsuario",
            width: "5%",
            cell: info => <DefaultCell>{info.getValue() || "-"}</DefaultCell>,
            isSorted: true,
        },
        {
            header: "nome",
            accessorKey: "nome",
            width: "20%",
            cell: info => <DefaultCell>{info.getValue() || "-"}</DefaultCell>,
            isSorted: true,
        },
        {
            header: "e-mail",
            accessorKey: "email",
            width: "20%",
            cell: info => <DefaultCell>{info.getValue() || "-"}</DefaultCell>,
            isSorted: true,
        },
        {
            header: "ativo",
            accessorKey: "ativo",
            width: "7%",
            cell: info => (
                <Checkbox
                    checked={info.getValue()}
                    disabled
                    color="primary"
                    inputProps={{ 'aria-label': 'Ativo' }}
                />
            ),
            isSorted: false,
        },
        {
            header: "bloqueado",
            accessorKey: "bloqueado",
            width: "7%",
            cell: info => (
                <Checkbox
                    checked={info.getValue()}
                    disabled
                    color="primary"
                    inputProps={{ 'aria-label': 'Bloqueado' }}
                />
            ),
            isSorted: false,
        },
        {
            header: "último login",
            accessorKey: "ultimoLogin",
            width: "10%",
            cell: info => <DefaultCell>{toDateTime(info.getValue()) || "-"}</DefaultCell>,
            isSorted: true,
        },
        {
            header: "funcionalidades",
            accessorKey: "perfils",
            isSorted: false,
            cell: info => (
                <Tooltip title="Funcionalidades">
                    <IconButton onClick={() => {
                        handleModalFuncionalidades(info.getValue())
                    }}
                        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                        <MDTypography variant="h4">
                            <Icon color="inherit" sx={{ marginRight: 1 }}>build</Icon>
                        </MDTypography>
                    </IconButton>
                </Tooltip>
            ),
            width: "6%",
            align: "center"
        },
        {
            header: "empresas",
            accessorKey: "clientes",
            isSorted: false,
            cell: info => {
                const empresas = info.getValue();
                return empresas && empresas.length > 0 ? (
                    <Tooltip title="Empresas">
                        <IconButton onClick={() => {
                            setDadosModalTable(empresas);
                            setIsModalTable(true);
                            setHeaderModal("Empresas");
                        }}>
                            <MDTypography variant="h4">
                                <Icon color="inherit" sx={{ marginRight: 1 }}>apartment</Icon>
                            </MDTypography>
                        </IconButton>
                    </Tooltip>
                ) : null;
            },
            width: "6%",
            align: "center"
        },
        {
            header: "Perfis",
            accessorKey: "perfils",
            isSorted: false,
            cell: info => (
                <Tooltip title="Perfis">
                    <IconButton onClick={() => {
                        setDadosModalTable(info.getValue());
                        setIsModalTable(true);
                        setHeaderModal("Perfis");
                    }}>
                        <MDTypography variant="h4">
                            <Icon color="inherit" sx={{ marginRight: 1 }}>groups</Icon>
                        </MDTypography>
                    </IconButton>
                </Tooltip>
            ),
            width: "6%",
            align: "center"
        },
        {
            header: "ações",
            isSorted: false,
            width: "10%",
            cell: ({ row }) => {
                let { id, inclusaoAlteracaoSistemaId } = row.original;
                return (
                    <>
                        <Tooltip title="Editar Usuário">
                            <IconButton
                                onClick={() => {
                                    setSelectedUserId(id);
                                    setIsCreateEdit(true);
                                }}>
                                <MDTypography variant="h4">
                                    <Icon color="inherit" sx={{ marginRight: 1 }}>edit</Icon>
                                </MDTypography>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Log">
                            <IconButton
                                onClick={() => handleOpenModal(inclusaoAlteracaoSistemaId)}>
                                <MDTypography variant="h4">
                                    <Icon color="inherit" sx={{ marginRight: 1 }}>search</Icon>
                                </MDTypography>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Resetar a Senha">
                            <IconButton
                                onClick={() => recoverPassword(row.original.email)}>
                                <MDTypography variant="h4">
                                    <Icon color="inherit" sx={{ marginRight: 1 }}>restart_alt</Icon>
                                </MDTypography>
                            </IconButton>
                        </Tooltip>
                        {isAdmin && (
                            <Tooltip title="Excluir Usuário">
                                <IconButton
                                    onClick={() => deleteUser(id)}>
                                    <MDTypography variant="h4">
                                        <Icon color="inherit" sx={{ marginRight: 1 }}>delete</Icon>
                                    </MDTypography>
                                </IconButton>
                            </Tooltip>
                        )}
                    </>
                )
            }
        },
    ], [isAdmin, darkMode]);

    const handleApplyFilter = () => {
        let filtered = [];
        Object.keys(filters).forEach(filterKey => {
            if (verifyVariable(filters[filterKey])) {

                if (!["perfils"].includes(filterKey) && filterKey !== 'ativo') {
                    filtered.push({ id: filterKey, value: filters[filterKey] });
                } else if (filterKey == "ativo") {
                    if (filters.ativo?.value != null) {
                        filtered.push({ id: filterKey, value: filters.ativo?.value });
                    }
                }
            }
        });

        setAplicar(true);
        fetchData({
            ...tableState,
            filtered,
            profileIdList: filters.perfils.map(e => e.id)
        });
    };

    const handleCleanFilter = () => {
        setFilters({
            nome: '',
            email: '',
            perfils: [],
            bloqueado: null,
            ativo: trueOrFalseEnum[1],
        })
        const updatedTableState = {
            pageSize: 10,
            pageIndex: 0,
            sorting: [],
            filtered: [],
            profileIdList: []
        };

        tableState.current = updatedTableState;
        let filtered = [];
        filtered.push({ id: 'ativo', value: true });
        fetchData({ ...tableState, filtered });
    };

    const handleOpenModal = (id) => {
        setSelectedIdInclusao(id);
        setIsLog(true);
    };

    const handleCloseModal = () => {
        setIsLog(false);
        setIsModalTable(false);
        setDadosModalTable(null);
        setSelectedIdInclusao(null);
        setHeaderModal(null);
        setDadosModalFuncinalidades(null);
        setIsModalFuncionalidades(false);
    };

    const handleRefresh = () => {
        setRefresh(true);
    };

    const handleModalFuncionalidades = (dados) => {
        setIsModalFuncionalidades(true);
        setDadosModalFuncinalidades(dados);
    };

    return (
        <Grid container display="flex" justifyContent="center">

            {isLog &&
                <MDViewLog
                    id={selectedIdInclusao}
                    show={isLog}
                    close={handleCloseModal}
                />
            }

            {isModalFuncionalidades &&
                <MDModalMenus
                    open={isModalFuncionalidades}
                    onClose={handleCloseModal}
                    dados={dadosModalFuncinalidades}
                />
            }

            {isModalTable &&
                <MDModalPerfis
                    dados={dadosModalTable}
                    show={isModalTable}
                    close={handleCloseModal}
                    header={headerModal}
                />
            }

            {isCreateEdit &&
                <Grid item xs={12} md={12}  >
                    <UserCreateEdit
                        userId={selectedUserId}
                        closeCreateEdit={backFunction}
                        refreshTable={handleRefresh} />
                </Grid>}

            <Grid item xs={12} md={12} sx={{ display: isCreateEdit ? "none" : "" }}>
                <Card
                    variant="outlined"
                    sx={{
                        px: 2,
                        py: 1.5,
                        mb: 2,
                        background: darkMode ? "#292C37" : "#fafafa",
                        borderColor: darkMode ? "#333" : "#ddd",
                    }}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={3}>
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Perfis</InputLabel>
                                <MDSelect
                                    loading={isLoadingPerfis}
                                    isMulti={true}
                                    getOptionValue={option => option.id}
                                    getOptionLabel={option => option.nome}
                                    name="profile-multi-select"
                                    placeholder="Todos"
                                    value={filters.perfils}
                                    options={profileOptions}
                                    onChange={selectedOptions => setFilters({ ...filters, perfils: selectedOptions })}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Bloqueado?</InputLabel>
                                <MDSelect
                                    isMulti={false}
                                    getOptionValue={(option) => option.id}
                                    getOptionLabel={(option) => option.name}
                                    name="bloqueado"
                                    placeholder="Ambos"
                                    value={trueOrFalseEnum.find(({ value }) => value == filters.bloqueado) || null}
                                    options={trueOrFalseEnum}
                                    onChange={({ value }) => {
                                        setFilters({ ...filters, bloqueado: value })
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Ativo</InputLabel>
                                <MDSelect
                                    isMulti={false}
                                    getOptionValue={(option) => option.id}
                                    getOptionLabel={(option) => option.name}
                                    name="ativo"
                                    placeholder="Ambos"
                                    value={filters.ativo}
                                    options={trueOrFalseEnum}
                                    onChange={selectedOptions => setFilters({ ...filters, ativo: selectedOptions })}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Nome</InputLabel>
                                <MDInput
                                    size="small"
                                    placeholder="Informe o nome..."
                                    value={filters?.nome}
                                    onChange={(e) => setFilters({ ...filters, nome: e.target.value })}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleApplyFilter();
                                        }
                                    }}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>E-mail</InputLabel>
                                <MDInput
                                    size="small"
                                    placeholder="Informe o e-mail..."
                                    value={filters?.email}
                                    onChange={(e) => setFilters({ ...filters, email: e.target.value })}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleApplyFilter();
                                        }
                                    }}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} display="flex" alignItems="center" justifyContent="space-between" pt={1}>
                                <MDBox>
                                    <MDButton
                                        size="medium"
                                        variant="contained"
                                        color="info"
                                        circular={false}
                                        iconOnly={false}
                                        onClick={() => {
                                            setSelectedUserId(null);
                                            setIsCreateEdit(true);
                                        }}>
                                        Adicionar Usuário
                                    </MDButton>
                                </MDBox>
                                <MDBox>
                                    <MDButton size="medium"
                                        sx={{ marginRight: 2 }}
                                        variant="contained"
                                        color="info"
                                        circular={false}
                                        iconOnly={false}
                                        onClick={handleCleanFilter}>
                                        Limpar filtro
                                    </MDButton>
                                    <MDButton size="medium" variant="contained" color="info" circular={false} iconOnly={false} onClick={handleApplyFilter}>
                                        Aplicar filtro
                                    </MDButton>
                                </MDBox>
                            </Grid>
                        </Grid>
                </Card>
                <DataTable
                    loading={loadingTable}
                    columnData={userColumns}
                    data={userData}
                    fetchData={fetchData}
                    totalPages={userDataPages}
                    totalRecords={recordsTotal}
                    tableState={tableState}
                    changeFilter={aplicar} />
            </Grid>
        </Grid>
    );
}
